/**
 * Helpers for 6-box OTP UIs.
 *
 * iOS Mail / SMS autofill inserts the full code into the first field as one
 * string. Handlers that keep only ``slice(-1)`` break that (last digit only).
 */

export const OTP_LENGTH = 6;

export function emptyOtpDigits(length = OTP_LENGTH) {
  return Array.from({ length }, () => '');
}

/** Strip non-digits and cap at OTP length. */
export function normalizeOtpDigits(raw, length = OTP_LENGTH) {
  return String(raw || '')
    .replace(/\D/g, '')
    .slice(0, length)
    .split('');
}

/**
 * Apply a change from one OTP cell (typing, paste, or iOS autofill).
 * Returns the next digit array.
 */
export function applyOtpCellInput(prev, index, raw, length = OTP_LENGTH) {
  const current = Array.isArray(prev) ? [...prev] : emptyOtpDigits(length);
  while (current.length < length) current.push('');
  const digits = normalizeOtpDigits(raw, length);

  if (!digits.length) {
    current[index] = '';
    return current.slice(0, length);
  }

  if (digits.length > 1) {
    // Autofill / paste: fill from this cell (usually index 0).
    for (let i = 0; i < length; i += 1) {
      current[i] = i < index ? current[i] : '';
    }
    digits.forEach((d, i) => {
      if (index + i < length) current[index + i] = d;
    });
    return current.slice(0, length);
  }

  current[index] = digits[0];
  return current.slice(0, length);
}

/** Focus index after applying a multi/single digit change. */
export function otpFocusAfterInput(index, raw, length = OTP_LENGTH) {
  const digits = normalizeOtpDigits(raw, length);
  if (!digits.length) return index;
  if (digits.length > 1) return Math.min(index + digits.length, length - 1);
  return Math.min(index + 1, length - 1);
}
